import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, isLanguage, type Language } from '@lightbm/shared';
import { STORE_KEYS, localStore } from '@/lib/local-store';
import { NAMESPACES, resources } from './resources';
import { configureZodErrors } from './zod-errors';

/**
 * Language precedence: the signed-in user's choice (USR-10), else the last choice on this
 * device, else the browser language, else English. Before sign-in, the company default
 * language applies when this device has no choice yet (see the sign-in page).
 */
function initialLanguage(): Language {
  const stored = localStore.get(STORE_KEYS.language);
  if (isLanguage(stored)) return stored;
  const browser = navigator.language?.slice(0, 2);
  return isLanguage(browser) ? browser : DEFAULT_LANGUAGE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: ['en', 'fr'],
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

document.documentElement.lang = i18n.language;
i18n.on('languageChanged', (language) => {
  document.documentElement.lang = language;
});

configureZodErrors(i18n);

/** Switches the interface language and remembers it on this device. */
export async function setLanguage(language: Language): Promise<void> {
  localStore.set(STORE_KEYS.language, language);
  if (i18n.language !== language) await i18n.changeLanguage(language);
}

export const hasStoredLanguage = () => isLanguage(localStore.get(STORE_KEYS.language));

export default i18n;
